import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Redirect, useHistory, useParams } from "react-router-dom";
import axios from "axios";
import {
  FaBook,
  FaBookOpen,
  FaChalkboardTeacher,
  FaEdit,
  FaHome,
  FaLayerGroup,
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaTrash,
  FaUserGraduate,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import myToast from "../Utils/MyToast";
import MdlStaffSubject from "../dialog/MdlStaffSubject";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  '"Segoe UI", system-ui, -apple-system, Roboto, "Helvetica Neue", Arial, sans-serif';

const FILTERS = [
  { id: "all", label: "All" },
  { id: "Main", label: "Main" },
  { id: "Optional", label: "Optional" },
];

//Created By Cursor
function uniqueClassesFromGetClass(classData = []) {
  const map = new Map();
  (classData || []).forEach((item) => {
    if (!item?.class_id || map.has(String(item.class_id))) return;
    map.set(String(item.class_id), {
      class_id: item.class_id,
      class_name: item.class_name || item.class_id,
    });
  });
  return Array.from(map.values()).sort((a, b) =>
    String(a.class_name).localeCompare(String(b.class_name))
  );
}

//Created By Cursor
function teachingClassIdsFromItem(item) {
  if (Array.isArray(item?.teaching_class_ids)) {
    return item.teaching_class_ids.map(String);
  }
  return String(item?.teaching_classes || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

//Created By Cursor
const MappingCard = memo(function MappingCard({
  item,
  index,
  busy,
  onEdit,
  onDelete,
}) {
  const classes = Array.isArray(item.teaching_classes_list)
    ? item.teaching_classes_list
    : [];
  const typeLabel = item.subject_type || "—";
  const isMain = typeLabel === "Main";
  const isOptional = typeLabel === "Optional";

  return (
    <article
      className={`ss2-card${isMain ? " ss2-card--main" : ""}${
        isOptional ? " ss2-card--optional" : ""
      }`}
      style={{ "--ss-i": index }}
    >
      <div className="ss2-card__top">
        <div className="ss2-card__icon" aria-hidden>
          <FaBookOpen />
        </div>
        <div className="ss2-card__title-wrap min-w-0">
          <h3 className="ss2-card__name">
            {item.subject_name || item.subject_id}
          </h3>
          <span
            className={`ss2-type${
              isMain ? " is-main" : isOptional ? " is-optional" : ""
            }`}
          >
            {typeLabel}
          </span>
        </div>
        <div className="ss2-card__actions">
          <button
            type="button"
            className="ss2-icon-btn"
            title="Edit"
            disabled={busy}
            onClick={() => onEdit(item)}
          >
            <FaEdit />
          </button>
          <button
            type="button"
            className="ss2-icon-btn ss2-icon-btn--danger"
            title="Remove"
            disabled={busy}
            onClick={() => onDelete(item)}
          >
            <FaTrash />
          </button>
        </div>
      </div>

      {/* Created By Cursor — polished teaching-classes block */}
      <div className="ss2-card__classes">
        <div className="ss2-card__classes-head">
          <span className="ss2-card__classes-label">
            <span className="ss2-card__classes-ico" aria-hidden>
              <FaLayerGroup />
            </span>
            Teaching classes
          </span>
          {classes.length > 0 ? (
            <span className="ss2-card__classes-count" aria-label={`${classes.length} classes`}>
              {classes.length}
            </span>
          ) : null}
        </div>
        {classes.length === 0 ? (
          <span className="ss2-chip ss2-chip--muted">No classes</span>
        ) : (
          <div className="ss2-chip-row" role="list">
            {classes.map((c) => {
              const label = c.class_name || c.class_id;
              const short = String(label).length <= 4;
              return (
                <span
                  key={c.class_id}
                  className={`ss2-chip${short ? " ss2-chip--short" : ""}`}
                  title={label}
                  role="listitem"
                >
                  {label}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </article>
  );
});

//Created By Cursor
export default function StaffSubject() {
  const history = useHistory();
  const loggedIn = localStorage.getItem("token") != null;
  const params = useParams();
  const routeStaffId = params.staff_id || params.Staff_id || "";

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [staffId, setStaffId] = useState(routeStaffId || "");
  const [staffQuery, setStaffQuery] = useState("");
  const [mapQuery, setMapQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [mappings, setMappings] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formInitial, setFormInitial] = useState({
    subject_id: "",
    subject_type: "Main",
    teaching_classes: [],
  });

  const selectedStaff = useMemo(
    () => staffList.find((s) => String(s.staff_id) === String(staffId)) || null,
    [staffList, staffId]
  );

  const filteredStaff = useMemo(() => {
    const q = staffQuery.trim().toLowerCase();
    if (!q) return staffList;
    return staffList.filter((s) => {
      const hay = `${s.staff_name || ""} ${s.staff_id || ""} ${
        s.department_name || ""
      }`.toLowerCase();
      return hay.includes(q);
    });
  }, [staffList, staffQuery]);

  const filteredMappings = useMemo(() => {
    const q = mapQuery.trim().toLowerCase();
    return mappings.filter((m) => {
      if (typeFilter !== "all" && m.subject_type !== typeFilter) return false;
      if (!q) return true;
      const classText = (m.teaching_classes_list || [])
        .map((c) => c.class_name || c.class_id)
        .join(" ");
      return `${m.subject_name || ""} ${m.subject_id || ""} ${classText}`
        .toLowerCase()
        .includes(q);
    });
  }, [mappings, mapQuery, typeFilter]);

  //Created By Cursor
  const loadLookups = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) return;
    const sessionId =
      SessionManager.getCurrentSession()?.[0]?.session_id || "";
    try {
      const [staffRes, subRes, classRes] = await Promise.all([
        axios.get(`${API_BASE()}getStaff.php`, {
          params: { school_code: schoolCode },
        }),
        axios.get(`${API_BASE()}getSubjectMasterList.php`, {
          params: { school_code: schoolCode },
        }),
        axios.get(`${API_BASE()}getClass.php`, {
          params: {
            school_code: schoolCode,
            session_id: sessionId || undefined,
          },
        }),
      ]);

      const staff =
        staffRes.data?.api_status === "OK" && Array.isArray(staffRes.data.staff)
          ? staffRes.data.staff
          : [];
      setStaffList(staff);

      const rawSubs = subRes.data?.data;
      const subs =
        subRes.data?.api_status === "OK" && Array.isArray(rawSubs)
          ? rawSubs
          : [];
      setSubjects(subs);

      const classData =
        classRes.data?.api_status === "OK"
          ? classRes.data.data?.class || []
          : [];
      setClasses(uniqueClassesFromGetClass(classData));
    } catch (err) {
      console.error("[StaffSubject] lookups:", err);
      myToast.notify_warning("Failed to load staff / subjects.");
    }
  }, []);

  //Created By Cursor
  const loadMappings = useCallback(async (sid, { soft = false } = {}) => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode || !sid) {
      setMappings([]);
      setLoading(false);
      setRefreshing(false);
      return;
    }
    if (soft) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await axios.get(`${API_BASE()}getStaffSubjects.php`, {
        params: { school_code: schoolCode, staff_id: sid },
      });
      if (res.data?.api_status === "OK" && Array.isArray(res.data.data)) {
        setMappings(res.data.data);
      } else {
        setMappings([]);
      }
    } catch (err) {
      console.error("[StaffSubject] mappings:", err);
      setMappings([]);
      myToast.notify_warning("Failed to load staff subjects.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    setStaffId(routeStaffId || "");
  }, [routeStaffId]);

  useEffect(() => {
    if (!staffId) {
      setMappings([]);
      setLoading(false);
      return;
    }
    loadMappings(staffId);
  }, [staffId, loadMappings]);

  //Created By Cursor
  const selectStaff = useCallback(
    (id) => {
      const v = id || "";
      setStaffId(v);
      setMapQuery("");
      setTypeFilter("all");
      history.replace(
        v
          ? `/AdminMain/StaffSubject/${encodeURIComponent(v)}`
          : "/AdminMain/StaffSubject"
      );
    },
    [history]
  );

  //Created By Cursor
  const openAdd = useCallback(() => {
    if (!staffId) {
      myToast.notify_warning("Select a staff member first.");
      return;
    }
    setEditing(null);
    setFormInitial({
      subject_id: "",
      subject_type: "Main",
      teaching_classes: [],
    });
    setModalOpen(true);
  }, [staffId]);

  //Created By Cursor
  const openEdit = useCallback((item) => {
    setEditing(item);
    setFormInitial({
      subject_id: item.subject_id || "",
      subject_type: item.subject_type || "Main",
      teaching_classes: teachingClassIdsFromItem(item),
    });
    setModalOpen(true);
  }, []);

  //Created By Cursor
  const saveMapping = useCallback(
    async (payload) => {
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode || !staffId) return;
      const subject_id = payload?.subject_id || "";
      const teaching_classes = Array.isArray(payload?.teaching_classes)
        ? payload.teaching_classes.map(String)
        : [];
      if (!subject_id) {
        myToast.notify_warning("Select a subject.");
        return;
      }
      if (!teaching_classes.length) {
        myToast.notify_warning("Select at least one teaching class.");
        return;
      }
      setSaving(true);
      try {
        const body = {
          school_code: schoolCode,
          staff_id: staffId,
          subject_id,
          subject_type: payload.subject_type || null,
          teaching_classes,
          action: editing ? "update" : "add",
        };
        if (editing) body.id = editing.id;

        const res = await axios.post(`${API_BASE()}saveStaffSubject.php`, body);
        if (res.data?.api_status === "OK") {
          myToast.notify_success(editing ? "Updated." : "Added.");
          setModalOpen(false);
          await loadMappings(staffId, { soft: true });
        } else {
          myToast.notify_warning(res.data?.api_remark || "Save failed.");
        }
      } catch (err) {
        console.error(err);
        myToast.notify_warning("Failed to save staff subject.");
      } finally {
        setSaving(false);
      }
    },
    [staffId, editing, loadMappings]
  );

  //Created By Cursor
  const deleteMapping = useCallback(
    async (item) => {
      if (!window.confirm(`Remove ${item.subject_name || item.subject_id}?`)) {
        return;
      }
      const schoolCode = SessionManager.getSchoolCode();
      setSaving(true);
      try {
        const res = await axios.post(`${API_BASE()}saveStaffSubject.php`, {
          school_code: schoolCode,
          id: item.id,
          action: "delete",
        });
        if (res.data?.api_status === "OK") {
          myToast.notify_success("Removed.");
          await loadMappings(staffId, { soft: true });
        } else {
          myToast.notify_warning(res.data?.api_remark || "Delete failed.");
        }
      } catch (err) {
        console.error(err);
        myToast.notify_warning("Failed to delete.");
      } finally {
        setSaving(false);
      }
    },
    [staffId, loadMappings]
  );

  const busy = loading || refreshing || saving;
  const typeSummary = useMemo(() => {
    let main = 0;
    let optional = 0;
    mappings.forEach((m) => {
      if (m.subject_type === "Main") main += 1;
      else if (m.subject_type === "Optional") optional += 1;
    });
    return { main, optional };
  }, [mappings]);

  const addableSubjects = useMemo(() => {
    if (editing) return subjects;
    const mapped = new Set(mappings.map((m) => String(m.subject_id)));
    return subjects.filter((s) => !mapped.has(String(s.subject_id)));
  }, [subjects, mappings, editing]);

  if (!loggedIn) return <Redirect to="/Login" />;

  return (
    <div
      className="content payroll-page sm-page ss-page ss2-page"
      style={{ fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header d-flex justify-content-between align-items-center"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header d-flex align-items-center gap-2">
          <button
            type="button"
            className="qb-home-btn"
            aria-label="Home"
            onClick={() => history.push("/AdminMain")}
          >
            <FaHome />
          </button>
          <ul className="breadcrumbs mb-0">
            <li>
              <button
                type="button"
                className="ss-crumb"
                onClick={() => history.push("/AdminMain/StaffManagement")}
              >
                Staff
              </button>
            </li>
            <li>
              <span style={{ color: COLORS.white }}>Staff Subject</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell ss2-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaChalkboardTeacher />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Staff Subject</h1>
              <p className="sm-hero__sub">
                Map teachers to subjects & classes for Timetable Builder
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className={`ss2-hero-icon${refreshing ? " is-spinning" : ""}`}
              title="Refresh"
              disabled={busy || !staffId}
              onClick={() => loadMappings(staffId, { soft: true })}
            >
              <FaSyncAlt />
            </button>
            <button
              type="button"
              className="ss2-btn ss2-btn--hero"
              disabled={!staffId || busy}
              onClick={openAdd}
            >
              <FaPlus />
              <span>Add subject</span>
            </button>
          </div>
        </header>

        <div className="ss2-body">
          <div className="ss2-layout">
            <aside className="ss2-staff-panel" aria-label="Staff list">
              <div className="ss2-panel-head">
                <h2>Staff</h2>
                <span>{filteredStaff.length}</span>
              </div>
              <label className="ss2-search">
                <FaSearch aria-hidden />
                <input
                  type="search"
                  value={staffQuery}
                  onChange={(e) => setStaffQuery(e.target.value)}
                  placeholder="Search staff…"
                  aria-label="Search staff"
                />
              </label>
              <div className="ss2-staff-list" role="listbox">
                {filteredStaff.length === 0 ? (
                  <p className="ss2-panel-empty">No staff found.</p>
                ) : (
                  filteredStaff.map((s) => {
                    const active = String(s.staff_id) === String(staffId);
                    return (
                      <button
                        key={s.staff_id}
                        type="button"
                        role="option"
                        aria-selected={active}
                        className={`ss2-staff-item${active ? " is-active" : ""}`}
                        onClick={() => selectStaff(s.staff_id)}
                      >
                        <span className="ss2-staff-item__avatar" aria-hidden>
                          <FaUserGraduate />
                        </span>
                        <span className="ss2-staff-item__text min-w-0">
                          <strong>{s.staff_name || s.staff_id}</strong>
                          <em>{s.department_name || s.staff_id}</em>
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </aside>

            <section className="ss2-main" aria-label="Subject mappings">
              {!staffId ? (
                <div className="ss2-empty ss2-empty--hero">
                  <div className="ss2-empty__icon" aria-hidden>
                    <FaUserGraduate />
                  </div>
                  <h3>Choose a staff member</h3>
                  <p>
                    Select someone from the list to view and map the subjects
                    they teach.
                  </p>
                </div>
              ) : (
                <>
                  <div className="ss2-staff-banner">
                    <div className="ss2-staff-banner__who">
                      <span className="ss2-staff-banner__avatar" aria-hidden>
                        <FaUserGraduate />
                      </span>
                      <div className="min-w-0">
                        <p className="ss2-staff-banner__label">Managing</p>
                        <h2 className="ss2-staff-banner__name">
                          {selectedStaff?.staff_name || staffId}
                        </h2>
                        {selectedStaff?.department_name ? (
                          <p className="ss2-staff-banner__dept">
                            {selectedStaff.department_name}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="ss2-btn ss2-btn--primary"
                      disabled={busy}
                      onClick={openAdd}
                    >
                      <FaPlus />
                      <span>Add subject</span>
                    </button>
                  </div>

                  <div className="ss2-metrics" aria-label="Summary">
                    <div className="ss2-metric ss2-metric--total">
                      <span className="ss2-metric__icon" aria-hidden>
                        <FaBook />
                      </span>
                      <div>
                        <em>Subjects</em>
                        <strong>{mappings.length}</strong>
                      </div>
                    </div>
                    <div className="ss2-metric ss2-metric--main">
                      <span className="ss2-metric__icon" aria-hidden>
                        <FaBookOpen />
                      </span>
                      <div>
                        <em>Main</em>
                        <strong>{typeSummary.main}</strong>
                      </div>
                    </div>
                    <div className="ss2-metric ss2-metric--optional">
                      <span className="ss2-metric__icon" aria-hidden>
                        <FaLayerGroup />
                      </span>
                      <div>
                        <em>Optional</em>
                        <strong>{typeSummary.optional}</strong>
                      </div>
                    </div>
                  </div>

                  <div className="ss2-toolbar">
                    <div className="ss2-filters" role="tablist">
                      {FILTERS.map((f) => (
                        <button
                          key={f.id}
                          type="button"
                          role="tab"
                          aria-selected={typeFilter === f.id}
                          className={`ss2-filter${
                            typeFilter === f.id ? " is-active" : ""
                          }`}
                          onClick={() => setTypeFilter(f.id)}
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                    <label className="ss2-search ss2-search--inline">
                      <FaSearch aria-hidden />
                      <input
                        type="search"
                        value={mapQuery}
                        onChange={(e) => setMapQuery(e.target.value)}
                        placeholder="Filter subjects…"
                        aria-label="Filter subjects"
                      />
                    </label>
                  </div>

                  {loading ? (
                    <div className="ss2-loading">
                      <MyLoader />
                    </div>
                  ) : mappings.length === 0 ? (
                    <div className="ss2-empty">
                      <div className="ss2-empty__icon" aria-hidden>
                        <FaBookOpen />
                      </div>
                      <h3>No subjects mapped yet</h3>
                      <p>
                        Add the subjects this staff member teaches, and which
                        classes they cover.
                      </p>
                      <button
                        type="button"
                        className="ss2-btn ss2-btn--primary"
                        disabled={busy}
                        onClick={openAdd}
                      >
                        <FaPlus />
                        <span>Add first subject</span>
                      </button>
                    </div>
                  ) : filteredMappings.length === 0 ? (
                    <div className="ss2-empty ss2-empty--soft">
                      <h3>No matches</h3>
                      <p>Try another filter or clear the search.</p>
                    </div>
                  ) : (
                    <div className="ss2-grid">
                      {filteredMappings.map((item, index) => (
                        <MappingCard
                          key={item.id || index}
                          item={item}
                          index={index}
                          busy={busy}
                          onEdit={openEdit}
                          onDelete={deleteMapping}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </section>
          </div>
        </div>
      </div>

      <MdlStaffSubject
        open={modalOpen}
        editing={Boolean(editing)}
        staffName={selectedStaff?.staff_name || staffId}
        subjects={addableSubjects}
        classes={classes}
        initial={formInitial}
        saving={saving}
        onClose={() => setModalOpen(false)}
        onSave={saveMapping}
      />
    </div>
  );
}
